import axios from "axios";
import { currentDateTimeString } from "../services/dateService";
import { getToken } from "./authApi";
import {ApiResponse, BaseCreateResponse, BaseInfo, BaseInfoList, BaseResponse, BaseResponseWithData, baseUrl, deleteWithAuth, GetParams, getWithAuth, getWithAuthAndParams, Guid, ListRespone, patchWithAuth, postWithAuth, postWithAuthWitoutParams} from "./baseApi";
import { MediaListResponse, MediaModel } from "./mediaApi";
import { DayOfWeek } from "./channelsApi";

export type PlayerBaseModel = {
    name : string,
    id : Guid
}

export enum PlayerStatus {
    Online = 0,
    Offline = 1,
    OutOfDayParting = 2
}

export type PlayerModel = 
{
    name : string,
    isOnline: boolean,
    lastSeen : string,
    id: string,
    lat: number,
    long: number,
    location: string,
    channel: BaseInfo | null,
    withActiveSchedule: boolean,
    playerStatus: PlayerStatus,
    version: string,
    isEnabled: boolean
}

export type PlayersResponse = 
{
    players : PlayerModel[]
} 

export type GetUnasignedPlayersRespone = {
    unassignedPlayers: PlayerBaseModel[]
}


export type PlayerUpdateChannelRequest = {
    channelId: Guid | null
}

export type UpdateConfigurationRequest = {
    tags : Guid[],
    isEnabled: boolean,
    channelId: Guid | undefined | null
    volume: number,
    dshowSize: string,
    dshowAspectRatio: string,
    isStreamEnabled: boolean,
    dshowVdev:string | null,
    dshowAdev:string | null,
    audioDelay: number | '',
    chromaFormat: string,
    skinId: Guid | null | undefined,
    tvChannelMappingProfileId: Guid | null,
    transcodeProfileId: Guid | null,
    loudnessTargetLevel: number | null,
    loudnessTruePeak: number | null,
    useProxyServer: boolean,
    isBespokeImageEnabled: boolean,
    isDownloadScheduleEnabled: boolean,
    skyQIP: string
}

export type PlayerConfiguration = {
    tags : Guid[],
    isEnabled: boolean,
    channelId: Guid,
    volume: number,
    defaultImageUrl: string,
    dshowSize: string,
    dshowAspectRatio: string,
    isStreamEnabled: boolean,
    dshowVdev: string | null,
    dshowAdev: string | null,
    audioDelay: number | '',
    chromaFormat: string,
    skinId: Guid,
    tvChannelMappingProfileId: Guid | null,
    transcodeProfileId: Guid | null,             
    loudnessTargetLevel: number | null,
    loudnessTruePeak: number | null,
    dolbyLoudnessTargetLevel: number,
    dolbyLoudnessTruePeak: number,
    useProxyServer: boolean,
    bespokeImageUrl: string,
    isBespokeImageEnabled: boolean,
    isDownloadScheduleEnabled: boolean,
    skyQIP: string
}

export type PlayerTechProfile = {
    playerName: string,
    ip: string,
    userAgent: string,
    location: string,
    postalCode: string,
    clientNote: string,
    playerId: string,
    horizontalResolution: number,
    verticalResolution: number,
    totalDiskSpace: number,
    availableDiskSpace: number,
    version: string
}

export type UpdatePlayerTechProfileRequest = {
    playerName: string,
    ip: string,
    userAgent: string,
    location: string,
    postalCode: string,
    clientNote: string
}

export type PlayerLog = {
    playerEvent: string,
    user: string,
    dateTime: string,
    comment: string
}

export type ListPlayerLogs = {
    pageSize: number,
    page: number,
    totalPages: number,
    items: PlayerLog[]
}

export type HealthStatisticEntry = {
    dateTime: string,
    value: number
}

export type PlayerHealth = {
    totalSpaceGB: number,
    totalRam: number,
    cpuUsage: HealthStatisticEntry[],
    gpuUsage: HealthStatisticEntry[],
    spaceUsage: HealthStatisticEntry[],
    heartbeats: HealthStatisticEntry[],
    ram: HealthStatisticEntry[],
}

export type ScreenShot = {
    screenShotUrl: string,
    screensShotPreviewUrl: string,
    dateTime: string
}

export interface SkinTimeslotRequest {
    name: string,
    start: string,
    end: string
}

export interface SkinTimeslotResponse extends SkinTimeslotRequest {
    id: Guid
}

export interface DownloadTimeslotRequest {
    name: string,
    startDate: string,
    endDate: string,
    startTime: string,
    endTime: string,
    monday: boolean,
    tuesday: boolean,
    wednesday: boolean,
    thursday: boolean,
    friday: boolean,
    saturday: boolean,
    sunday: boolean
}

export interface DownloadTimeslotResponse extends DownloadTimeslotRequest {
    id: Guid,
    playerId: string,
    volume: number,
    isPriority: boolean
}

export type PlayerDevice = {
    name: string
}

export interface VolumeProfile {
    id: Guid,
    name: string,
    startDate: string,
    endDate: string | null,
    startTime: string,
    endTime: string,
    monday: boolean,
    tuesday: boolean,
    wednesday: boolean,
    thursday: boolean,
    friday: boolean,
    saturday: boolean,
    sunday: boolean,
    volume: number,
    isPriority: boolean,
}

export type VolumeProfileRequest = Omit<VolumeProfile,'id'>

export enum PlayerScheduleOvviredeType {
        Enabled = 0, // blue
        Disabled = 1, // red
        EnabledInSite = 2, // green
}

export type PlayerTimeSlotOverrideModel = {
    id: Guid,
    dayOfWeek : DayOfWeek,
    hour : number,
    minute: number,
    type: PlayerScheduleOvviredeType
}

export type PlayerTimeSlotOverrideRequest = {
    dayOfWeek : DayOfWeek,
    hour : number,
    minute: number,
}

export type PlayersMonitoringScreenshots = {
    players: PlayerWithScreenshots[]
}

export type PlayerWithScreenshots = {
    id:Guid,
    playerName: string | null,
    screenshots: {
        screenshotUrl: string,
        previewUrl: string,
        dateTime: string,
        siteInfo: string,
        isOffline: boolean,
        priorityListId: Guid | null,
        streamId:Guid | null,
    }[]
}


export type AdvertisingInfo = {
    slotDuration: number,
    maxDuration: number,
    minDuration: number,
}

export type PrivacyInfo = {
    tracking: boolean,
    gdpr: boolean,
}

export type ReachTrackerInfo = {
    monday: number,
    tuesday: number,
    wednesday: number,
    thursday: number,
    friday: number,
    saturday: number,
    sunday: number
}

export type PlayNowOfflineList = {
    playNowId: Guid,
    isEnabled: boolean,
    name: string,
    hotKey: number
}

export type OfflineModeToggleRequest = {
    playNowId: Guid
}

export type PlayerMonitor = {
    playerName: string,
    networkName: string,
    cpuUsage:  number,
    gpuUsage: number,
    availableSpaceBytes: number,
    totalSpaceBytes: number,
    latestHeartbeat: string,
    version: string,
    memoryUsedKB: number,
    totalMemoryKB: number
}

export type PlayerMonitorRequest = {
    searchString: string,
    networks: Guid[]
}

export enum SkyQCommandType {
    Power = 0
}

export type SendSkyQCommandRequest = {
    command: SkyQCommandType
}

export const getPlayers = async () : Promise<PlayersResponse> => {
    var result = await getWithAuth<PlayersResponse>({url: `${baseUrl}players`});

    if(result.type == "succes") return result.data;
    return { players : new Array<PlayerModel>() }
}

export const getPlayersShort = async() : Promise<ApiResponse<BaseInfoList>> => {
    return await getWithAuth<BaseInfoList>({url : `${baseUrl}players/short`});
}

export const unassigneChannelFromPlayer = async( playerId: Guid) : Promise<ApiResponse<BaseResponse>> => {
    let request:PlayerUpdateChannelRequest = {channelId: null};
    return await patchWithAuth<PlayerUpdateChannelRequest, BaseResponse>(`${baseUrl}players/${playerId}/channel`, request);
}

export const getPlayerConfiguration = async(playerId: Guid) : Promise<ApiResponse<PlayerConfiguration>> => {
    return await getWithAuth<PlayerConfiguration>({ url: `${baseUrl}players/${playerId}/configuration`});
}

export const getPlayerTechProfile = async(playerId: Guid) : Promise<ApiResponse<PlayerTechProfile>> => {
    return await getWithAuth<PlayerTechProfile>({ url: `${baseUrl}players/${playerId}/techprofile`});
}

export const getPlayerLogs = async(playerId: Guid, page: number) : Promise<ApiResponse<ListPlayerLogs>> => {
    let params = new GetParams();
    params.Add("page",page+'')
    return await getWithAuthAndParams<ListPlayerLogs>( `${baseUrl}players/${playerId}/logs`, params);
}

export const updateConfuguration = async( playerId: Guid, request: UpdateConfigurationRequest) : Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<UpdateConfigurationRequest, BaseResponse>(`${baseUrl}players/${playerId}/configuration`, request);
}

export const updateTechProfile = async( playerId: Guid, request: UpdatePlayerTechProfileRequest) : Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<UpdatePlayerTechProfileRequest, BaseResponse>(`${baseUrl}players/${playerId}/techprofile`, request);
}

export const getUnassignedPlayers = async (exceptChannelId : Guid) : Promise<ApiResponse<GetUnasignedPlayersRespone>> => {
    const params = new GetParams();
    if(!!exceptChannelId) params.Add("exceptChannelId", exceptChannelId);
    var result = await getWithAuthAndParams<GetUnasignedPlayersRespone>(`${baseUrl}players/unassigned`, params);

    return result;
}

export const getPlayerHealth = async(playerId: Guid, start: string, end: string) : Promise<ApiResponse<BaseResponseWithData<PlayerHealth>>> => {
    const params = new GetParams();    
    params.Add("start", start);
    params.Add("end", end);
    return await getWithAuthAndParams<BaseResponseWithData<PlayerHealth>>(`${baseUrl}players/${playerId}/health`, params);
}


export const addBackgroundImage = async (formData: any) : Promise<string> => {
    await axios.post(`${baseUrl}players/backgroundImage`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization' : getToken()
        }, 
    });

    return "ok"
}

export const addBespokeImage = async (formData: any) : Promise<string> => {
    await axios.post(`${baseUrl}players/bespokeImage`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization' : getToken()
        }, 
    });

    return "ok"
}

export const deletePlayer = async (playerId: Guid): Promise<ApiResponse<BaseResponse>> => {
    return await deleteWithAuth<BaseResponse>(`${baseUrl}players`, playerId);
}

export const getScreenshots = async (playerId: Guid): Promise<ApiResponse<ListRespone<ScreenShot>>>=>{
    return await getWithAuth<ListRespone<ScreenShot>>({url: `${baseUrl}players/${playerId}/screenshots`});
}

export const takeScreenshot = async (playerId: Guid): Promise<ApiResponse<BaseResponse>>=>{
    return await postWithAuthWitoutParams<BaseResponse>(`${baseUrl}players/${playerId}/screenshots`);
}

export const getMediaForPlayer = async (playerId:Guid): Promise<ApiResponse<ListRespone<MediaModel>>> => {
    return await getWithAuth<ListRespone<MediaModel>>({url : `${baseUrl}players/${playerId}/media`});
}

export const getSkinTimeslots = async (id: Guid): Promise<ApiResponse<ListRespone<SkinTimeslotResponse>>> => {
    return await getWithAuth<ListRespone<SkinTimeslotResponse>>({url : `${baseUrl}players/${id}/skintimeslots`});
}

export const postSkinTimeslots = async (request: SkinTimeslotRequest, id:Guid): Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<SkinTimeslotRequest,BaseCreateResponse>(`${baseUrl}players/${id}/skintimeslots`, request);
}

export const deleteSkinTimeslot = async (playerId:Guid, slotId:Guid):Promise<ApiResponse<BaseResponse>> => {
    return await deleteWithAuth<BaseResponse>(`${baseUrl}players/${playerId}/skintimeslots`, slotId);
}

export const getPlayerAudioDevices = async(playerId: Guid) : Promise<ApiResponse<ListRespone<PlayerDevice>>> => {
    return await getWithAuth<ListRespone<PlayerDevice>>({ url: `${baseUrl}players/${playerId}/audio-devices`});
}

export const getPlayerVideoDevices = async(playerId: Guid) : Promise<ApiResponse<ListRespone<PlayerDevice>>> => {
    return await getWithAuth<ListRespone<PlayerDevice>>({ url: `${baseUrl}players/${playerId}/video-devices`});
}



///////
export interface TvChannelOverrided {
    id: Guid,
    tvChannelName: string,
    tvChannelId: Guid,
    originalValue: string,
    playerValue: string
}

export interface TvChannelOverrideCreateRequest {
    tvChannelId: Guid,
    command: string
}

export interface TvChannelOverrideUpdateRequest {
    command: string
}

export const getTVChannelOverrides = async(id: Guid) : Promise<ApiResponse<ListRespone<TvChannelOverrided>>> => {
    return await getWithAuth<ListRespone<TvChannelOverrided>>({url : `${baseUrl}players/${id}/tvchannel-overrides`});
}

export const createTVChannelOverrride = async(id: Guid, request : TvChannelOverrideCreateRequest) : Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<TvChannelOverrideCreateRequest,BaseCreateResponse>(`${baseUrl}players/${id}/tvchannel-overrides`, request);
}

export const updateTVChannelOverride = async(id : Guid, overrideId: Guid, request : TvChannelOverrideUpdateRequest) : Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<TvChannelOverrideUpdateRequest,BaseResponse>(`${baseUrl}players/${id}/tvchannel-overrides/${overrideId}`, request);
}

export const deleteTVChannelOverride = async (id : Guid, overrideId: Guid) : Promise<ApiResponse<BaseResponse>> =>{
    return await deleteWithAuth<BaseResponse>(`${baseUrl}players/${id}/tvchannel-overrides`, overrideId)
}

export const restartPlayer = async(id: Guid) : Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<{},BaseCreateResponse>(`${baseUrl}players/${id}/restart`, {});
}

export const forceIrCommand = async(id: Guid) : Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<{},BaseCreateResponse>(`${baseUrl}players/${id}/force-ir-command`, {});
}

export const sendSkyQCommand = async(id: Guid, request: SendSkyQCommandRequest) : Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<SendSkyQCommandRequest, BaseCreateResponse>(`${baseUrl}players/${id}/skyq-command`, request);
}

//Volume Profile
export const getVolumeProfiles = async(id: Guid) : Promise<ApiResponse<ListRespone<VolumeProfile>>> => {
    return await getWithAuth<ListRespone<VolumeProfile>>({url : `${baseUrl}players/${id}/volume-profiles`});
}

export const createVolumeProfile = async(id: Guid, request : VolumeProfileRequest) : Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<VolumeProfileRequest,BaseCreateResponse>(`${baseUrl}players/${id}/volume-profiles`, request);
}

export const updateVolumeProfile = async(id : Guid, profileId: Guid, request : VolumeProfileRequest) : Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<VolumeProfileRequest,BaseResponse>(`${baseUrl}players/${id}/volume-profiles/${profileId}`, request);
}

export const deleteVolumeProfile = async (id : Guid, profileId: Guid) : Promise<ApiResponse<BaseResponse>> =>{
    return await deleteWithAuth<BaseResponse>(`${baseUrl}players/${id}/volume-profiles`, profileId)
}

export const getPlayerScheduleOverride = async (id: Guid): Promise<ApiResponse<ListRespone<PlayerTimeSlotOverrideModel>>> => {
    return await getWithAuth<ListRespone<PlayerTimeSlotOverrideModel>>({url : `${baseUrl}players/${id}/schedule-ovverides`});
}

export const postPlayerScheduleOverride = async (request: PlayerTimeSlotOverrideRequest, id:Guid): Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<PlayerTimeSlotOverrideRequest,BaseCreateResponse>(`${baseUrl}players/${id}/schedule-ovverides`, request);
}

export const getPlayersScreenshots = async (request:{playerIds:Guid[]}): Promise<ApiResponse<BaseResponseWithData<PlayersMonitoringScreenshots>>> => {
    return await postWithAuth<{playerIds:Guid[]},BaseResponseWithData<PlayersMonitoringScreenshots>>(`${baseUrl}players/screenshot-dashboard`, request)
}

export const getAdvertisingInfo = async(id: Guid) : Promise<ApiResponse<BaseResponseWithData<AdvertisingInfo>>> => {
    return await getWithAuth<BaseResponseWithData<AdvertisingInfo>>({url : `${baseUrl}players/${id}/ap`});
}

export const patchAdvertisingInfo = async(id : Guid, request : AdvertisingInfo) : Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<AdvertisingInfo,BaseResponse>(`${baseUrl}players/${id}/ap`, request);
}

export const getPrivacyInfo = async(id: Guid) : Promise<ApiResponse<BaseResponseWithData<PrivacyInfo>>> => {
    return await getWithAuth<BaseResponseWithData<PrivacyInfo>>({url : `${baseUrl}players/${id}/privacy`});
}

export const patchPrivacyInfo = async(id : Guid, request : PrivacyInfo) : Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<PrivacyInfo,BaseResponse>(`${baseUrl}players/${id}/privacy`, request);
}

export const getReachTrackerInfo = async(id: Guid) : Promise<ApiResponse<BaseResponseWithData<ReachTrackerInfo>>> => {
    return await getWithAuth<BaseResponseWithData<ReachTrackerInfo>>({url : `${baseUrl}players/${id}/reach-tracker-profile`});
}

export const patchReachTrackerInfo = async(id : Guid, request : ReachTrackerInfo) : Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<ReachTrackerInfo,BaseResponse>(`${baseUrl}players/${id}/reach-tracker-profile`, request);
}

export const getPlayNowOffline = async(id: Guid) : Promise<ApiResponse<ListRespone<PlayNowOfflineList>>> => {
    return await getWithAuth<ListRespone<PlayNowOfflineList>>({url : `${baseUrl}players/${id}/playnow-offline`});
}

export const offlineModeEnable = async(id : Guid, request : OfflineModeToggleRequest) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<OfflineModeToggleRequest,BaseResponse>(`${baseUrl}players/${id}/playnow-offline/enable`, request);
}

export const offlineModeDisable = async(id : Guid, request : OfflineModeToggleRequest) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<OfflineModeToggleRequest,BaseResponse>(`${baseUrl}players/${id}/playnow-offline/disable`, request);
}

export const getDownloadTimeslots = async (id: Guid): Promise<ApiResponse<ListRespone<DownloadTimeslotResponse>>> => {
    return await getWithAuth<ListRespone<DownloadTimeslotResponse>>({url : `${baseUrl}players/${id}/download-schedule`});
}

export const postDownloadTimeslots = async (request: DownloadTimeslotRequest, id:Guid): Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<DownloadTimeslotRequest,BaseCreateResponse>(`${baseUrl}players/${id}/download-schedule`, request);
}

export const deleteDownloadTimeslot = async (playerId:Guid, slotId:Guid):Promise<ApiResponse<BaseResponse>> => {
    return await deleteWithAuth<BaseResponse>(`${baseUrl}players/${playerId}/download-schedule`, slotId);
}

export const getPlayerSystemMonitor = async (request: PlayerMonitorRequest): Promise<ApiResponse<ListRespone<PlayerMonitor>>> => {
    return await postWithAuth<PlayerMonitorRequest, ListRespone<PlayerMonitor>>(`${baseUrl}players/player-system-monitor`, request);
}
